/**
 * 雲端帳號區塊(學術風)—— 掛在設定頁的「☁️ 雲端帳號」section。
 *
 * 設計鐵則(boss 2026-06-08):教學/家長端要乾淨專業 → 紙感、serif 標題、單一
 * 學術綠重點色、無彩光 / 無音效 / 無 mascot。沿用設定頁既有的 .settings-section /
 * .primary-btn / .secondary-btn class,只加極少量 scoped 樣式(信箱輸入框 + 狀態列)。
 *
 * 行為:
 *   - 未登入:Email 輸入框 + 「寄登入連結」(signInWithEmail / magic link)+
 *     「用 Google 登入」(signInWithGoogle,先放著,Google 設好才會通)。
 *   - 已登入:顯示 email + 同步狀態 + 「登出」。
 *   - `isCloudEnabled()` 為 false → 整塊不渲染(回傳不 append 任何 section),
 *     設定頁完全照舊(向後相容)。
 *
 * 純查詢/動作都吞錯,不阻斷設定頁其他區塊。登入狀態變化(ef:account-changed)
 * 觸發整塊重繪。data-testid 供測試/E2E 抓節點。
 */
import {
  isCloudEnabled,
  getCurrentUser,
  signInWithEmail,
  signInWithGoogle,
  signOut,
} from '../shared/supabase';
import { ACCOUNT_CHANGED_EVENT } from '../shared/account';

const ACCOUNT_STYLE_ID = 'ef-account-style';

function ensureAccountStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(ACCOUNT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = ACCOUNT_STYLE_ID;
  style.textContent = `
.account-section .account-row { display: flex; flex-direction: column; gap: 10px; }
.account-section .account-hint {
  font-size: 13px;
  color: var(--ef-sub);
  line-height: 1.5;
  margin: 0;
}
.account-section .account-email-input {
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  font-size: 15px;
  font-family: var(--ef-sans);
  color: var(--ef-ink);
  background: var(--ef-paper);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-sm);
}
.account-section .account-email-input:focus-visible {
  outline: none;
  border-color: var(--ef-green);
  box-shadow: 0 0 0 3px rgba(31, 111, 92, 0.16);
}
.account-section .account-btn-row { display: flex; gap: 8px; flex-wrap: wrap; }
.account-section .account-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--ef-sub);
  font-size: 12px;
  letter-spacing: 1px;
  margin: 2px 0;
}
.account-section .account-divider::before,
.account-section .account-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--ef-line);
}
.account-section .account-status {
  min-height: 1.2em;
  font-size: 13px;
  color: var(--ef-sub);
}
.account-section .account-status.is-error { color: #b3261e; }
.account-section .account-status.is-ok { color: var(--ef-green-dark); }

/* 已登入卡:身分列 */
.account-section .account-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: var(--ef-note);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-sm);
}
.account-section .account-avatar {
  flex: 0 0 auto;
  width: 38px; height: 38px;
  display: grid; place-items: center;
  border-radius: 50%;
  background: var(--ef-green);
  color: #fff;
  font-family: var(--ef-serif);
  font-size: 18px;
  font-weight: 600;
}
.account-section .account-identity-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.account-section .account-identity-label { font-size: 11px; letter-spacing: 1px; color: var(--ef-sub); }
.account-section .account-email {
  font-family: var(--ef-sans);
  font-size: 15px;
  color: var(--ef-ink);
  font-weight: 600;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
`;
  document.head.appendChild(style);
}

/** 極簡 email 格式檢查 —— 只擋明顯無效,真正驗證交給 Supabase。 */
function looksLikeEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

function setStatus(el: HTMLElement, msg: string, tone: 'ok' | 'error' | '' = ''): void {
  el.textContent = msg;
  el.classList.remove('is-error', 'is-ok');
  if (tone === 'ok') el.classList.add('is-ok');
  else if (tone === 'error') el.classList.add('is-error');
}

/**
 * 渲染雲端帳號 section 並 append 到 `parent`。
 *
 * @returns 清理函式(移除 ef:account-changed listener);雲端未啟用時為 no-op
 *          且不 append 任何節點。
 */
export function renderAccountSection(parent: HTMLElement): () => void {
  // 雲端未設定 → 完全不渲染(向後相容:設定頁照舊)。
  if (!isCloudEnabled()) return () => {};

  ensureAccountStyle();

  const section = document.createElement('div');
  section.className = 'settings-section account-section';
  section.setAttribute('data-testid', 'account-section');
  parent.appendChild(section);

  /** 依登入狀態重畫整塊 section 內容。 */
  async function paint(): Promise<void> {
    let user: Awaited<ReturnType<typeof getCurrentUser>> = null;
    try {
      user = await getCurrentUser();
    } catch {
      user = null;
    }
    section.innerHTML = user
      ? signedInMarkup(user.email ?? '(無 email)')
      : signedOutMarkup();
    if (user) wireSignedIn(section);
    else wireSignedOut(section);
  }

  void paint();

  // 登入/登出 → 重繪。account.ts 在 onSignedIn/onSignedOut 廣播此事件。
  const onChange = (): void => { void paint(); };
  window.addEventListener(ACCOUNT_CHANGED_EVENT, onChange);

  return () => window.removeEventListener(ACCOUNT_CHANGED_EVENT, onChange);
}

// ── markup ────────────────────────────────────────────────────────────────

function signedOutMarkup(): string {
  return `
    <h3>☁️ 雲端帳號</h3>
    <div class="account-row">
      <p class="account-hint">
        登入後,孩子的學習進度會綁到你的帳號、跨裝置同步。
        每個帳號的資料完全獨立、互不相見。
        <br>💡 直接用你的 <b>Gmail</b> 信箱登入,就是用 Google 帳號區分不同人。
      </p>
      <input type="email" inputmode="email" autocomplete="email"
        class="account-email-input" id="account-email-input"
        data-testid="account-email-input"
        placeholder="輸入你的 Email" />
      <div class="account-btn-row">
        <button class="primary-btn" id="account-email-btn" data-testid="account-email-btn" disabled>
          ✉️ 寄登入連結
        </button>
      </div>
      <div class="account-divider">或</div>
      <div class="account-btn-row">
        <button class="secondary-btn" id="account-google-btn" data-testid="account-google-btn">
          使用 Google 登入
        </button>
      </div>
      <span class="account-status" id="account-status" data-testid="account-status"></span>
    </div>
  `;
}

function signedInMarkup(email: string): string {
  const initial = (email.trim()[0] ?? '?').toUpperCase();
  return `
    <h3>☁️ 雲端帳號</h3>
    <div class="account-row">
      <div class="account-identity" data-testid="account-identity">
        <div class="account-avatar" aria-hidden="true">${initial}</div>
        <div class="account-identity-text">
          <span class="account-identity-label">已登入</span>
          <span class="account-email" id="account-email-display" data-testid="account-email-display"
            title="${escapeAttr(email)}">${escapeHtml(email)}</span>
        </div>
      </div>
      <span class="account-status is-ok" id="account-status" data-testid="account-status">進度會自動同步到雲端</span>
      <div class="account-btn-row">
        <button class="secondary-btn" id="account-signout-btn" data-testid="account-signout-btn">登出</button>
      </div>
    </div>
  `;
}

// ── wiring ──────────────────────────────────────────────────────────────────

function wireSignedOut(section: HTMLElement): void {
  const input = section.querySelector('#account-email-input') as HTMLInputElement | null;
  const emailBtn = section.querySelector('#account-email-btn') as HTMLButtonElement | null;
  const googleBtn = section.querySelector('#account-google-btn') as HTMLButtonElement | null;
  const status = section.querySelector('#account-status') as HTMLElement | null;
  if (!input || !emailBtn || !googleBtn || !status) return;

  const refresh = (): void => { emailBtn.disabled = !looksLikeEmail(input.value); };
  input.addEventListener('input', refresh);
  input.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !emailBtn.disabled) emailBtn.click();
  });

  emailBtn.addEventListener('click', async () => {
    const email = input.value.trim();
    if (!looksLikeEmail(email)) {
      setStatus(status, '請輸入有效的 Email', 'error');
      return;
    }
    emailBtn.disabled = true;
    setStatus(status, '寄送中…', '');
    try {
      const { error } = await signInWithEmail(email);
      if (error) {
        setStatus(status, `寄送失敗:${error}`, 'error');
        emailBtn.disabled = false;
      } else {
        setStatus(status, `已寄出登入連結到 ${email},請收信點連結完成登入。`, 'ok');
        // 保持 disabled —— 避免重複轟炸;使用者改 email 會重新啟用。
      }
    } catch (err) {
      setStatus(status, `寄送失敗:${(err as Error).message}`, 'error');
      emailBtn.disabled = false;
    }
  });

  googleBtn.addEventListener('click', async () => {
    setStatus(status, '轉跳 Google 登入…', '');
    try {
      const { error } = await signInWithGoogle();
      if (error) setStatus(status, `Google 登入失敗:${error}`, 'error');
      // 成功會整頁轉跳,不需後續處理。
    } catch (err) {
      setStatus(status, `Google 登入失敗:${(err as Error).message}`, 'error');
    }
  });
}

function wireSignedIn(section: HTMLElement): void {
  const signoutBtn = section.querySelector('#account-signout-btn') as HTMLButtonElement | null;
  const status = section.querySelector('#account-status') as HTMLElement | null;
  if (!signoutBtn) return;

  signoutBtn.addEventListener('click', async () => {
    signoutBtn.disabled = true;
    if (status) setStatus(status, '登出中…', '');
    try {
      await signOut();
      // onAuthChange → account.ts → ef:account-changed → paint() 會把畫面切回未登入。
    } catch (err) {
      if (status) setStatus(status, `登出失敗:${(err as Error).message}`, 'error');
      signoutBtn.disabled = false;
    }
  });
}

// ── escape helpers(email 來自 auth provider,仍防禦性轉義) ─────────────────

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c),
  );
}
function escapeAttr(s: string): string {
  return escapeHtml(s);
}
